import { TranslateService } from "@ngx-translate/core";
import { Locale, localeData,  IntlService, CldrIntlService} from "@progress/kendo-angular-intl";
import { BehaviorSubject, Observable,  Subject } from "rxjs";
import { FormatWidth, getLocaleDateFormat, registerLocaleData } from "@angular/common";
import { Injectable } from "@angular/core";
import { GlobalEnumsAceSprache, LocalizedTextDisplayModel, PortalkonfigurationDisplayModel, UserSettingsDisplayModel } from "@/api/model-index";
import { UserSettingsService } from '@/api/service-index';
import { ExtendedAuthenticationService } from '@/core/components/authentication/services/extended-authentication.service';
import { Constants } from '../../constants';

const languageKey = 'language';

@Injectable({
    providedIn: 'root'
})

export class I18nService {

    static Instance: I18nService;
    defaultLanguage: string;

    public supportedLanguages: string[] = Constants.LANG_SUPPORTED_LANGUAGES;

    protected _languageChangedSource = new Subject<string>();
    protected _languageChangedBackendSource = new Subject<string>();
    public languageChanged$ = this._languageChangedSource.asObservable();
    public languageChangedBackend$ = this._languageChangedBackendSource.asObservable();

    protected portalkonfigurationSubject: BehaviorSubject<PortalkonfigurationDisplayModel | null> = new BehaviorSubject<PortalkonfigurationDisplayModel | null>(null);

    constructor(public translateService: TranslateService, 
        protected usersettingsService: UserSettingsService,
        protected authService: ExtendedAuthenticationService,
        public intlService: IntlService) 
    {
        this.translateService.defaultLang = this.supportedLanguages[0];;
        this.defaultLanguage = translateService.defaultLang;   
        
        this.languageImports();

        this.translateService.use(localStorage.getItem(languageKey) || (<CldrIntlService>this.intlService).localeId ||this.translateService.defaultLang);

        I18nService.Instance = this;
        (<CldrIntlService>this.intlService).localeId = this.translateService.currentLang;
    }

    protected languageImports(){
        var test =  Constants.LANG_SUPPORTED_LANGUAGES.forEach(async value => {
            let importVar = Constants.LANG_LANGUAGE_IMPORTPATHS.find(x => x.localeId == value);
            const lang_core = importVar?.coreJson;
            const lang_customized = importVar?.customizedJson;

            const language = Object.assign({}, lang_core, lang_customized);

            // Embed languages to avoid extra HTTP requests
            this.translateService.setTranslation(value, language);

            const localeLanguage = importVar?.locale;
            const localeLanguageExtra = importVar?.localeextra;
            
            registerLocaleData(localeLanguage, localeLanguageExtra);
        })
    }

    /**
     * Gets the current language.
     * @return The current language code.
     */
    public get language(): string {
        //workaround until there is a better solution in the core project for dealing with the localizedDisplayModel.Find functions which are everywhere.
        var lang = this.translateService.currentLang || (<CldrIntlService>this.intlService).localeId||  this.defaultLanguage ;
        return lang;
    }

    /**
     * Sets the current language.
     * Note: The current language is saved to the local storage.
     * If no parameter is specified, the language is loaded from local storage (if present).
     * @param language The IETF language code to set.
     */
    public set language(language: string) {
        language = language || localStorage.getItem(languageKey) || this.defaultLanguage;
        (<CldrIntlService>this.intlService).localeId = language;
        
        this.translateService.use(language);
        this._languageChangedSource.next();
        localStorage.setItem(languageKey, this.translateService.currentLang);

        //if language has changed -> changed Language info in Externerbenutzer
        var usersettingsmodel = new UserSettingsDisplayModel();
        usersettingsmodel.sprache = this.GetCurrLanguageAsEnum();
        
        if(this.authService.isAuthenticated()){
            this.usersettingsService.apiUserSettingsPost({body: usersettingsmodel}).subscribe(val => {
            
                this._languageChangedBackendSource.next();
            });
        }
    }

    public get(key: string | string[], interpolateParams?: Object): Observable<any> {
        return this.translateService.get(key, interpolateParams);
    }

    public instant(key: string | string[], interpolateParams?: Object): any {
        return this.translateService.instant(key, interpolateParams);
    }

    public instantForSpecificLanguage(key:string, localeId: string, interpolateParams?: Object): any{
        return this.translateService.getParsedResult(this.translateService.translations[localeId], key, interpolateParams)
    }

    
    public GetCurrLanguageAsEnum(): GlobalEnumsAceSprache | undefined{
        try{
            if(this.language.startsWith("en")){
                return GlobalEnumsAceSprache.Englisch;
            }
            else if(this.language.startsWith("de")){
                return GlobalEnumsAceSprache.Deutsch;
            }
        }
        catch{
            this.language = this.defaultLanguage;
        }
        return GlobalEnumsAceSprache.Deutsch;
        
    }
    
    public SetCurrLanguageFromEnum(enumValue: GlobalEnumsAceSprache){
        this.language = Constants.LANG_LANGUAGE_DISPLAY_ENUM.find(x => x.enumValue == enumValue)?.localeId ?? this.defaultLanguage;
    }

    public GetCurrentShortDateFormat(fullYear?: boolean) {
        return getLocaleDateFormat( this.language, FormatWidth.Short ) + (fullYear ? "yy" : "");
    }

    //override for custom Chat Date Format
    public GetDateFormatForChatMessages(): string {
        return (Constants.LANG_DATEFORMATS_FOR_CHAT_MESSAGES.find(x => x.localeId == this.language)?.format ?? "");
    }

    public getLanguageDisplayName(lang: string): string{
        return (Constants.LANG_LANGUAGE_DISPLAY_ENUM.find(x => x.localeId == lang)?.displayName ?? 'Undefined Language');
    }
    
    // can be used instead of the localizedDisplayModel.find(x => x.locale == this.languageService.language).text 
    // use the following regex expression to find all of the cases that should be replaced:
    // ----  \.find\(.*\.localeCode.*\.language  ----
    public getCurrentLocalizedTextDisplayModel(list: LocalizedTextDisplayModel[] | null | undefined): LocalizedTextDisplayModel | undefined {
        if(list){
            var localized = list?.find(text => text.localeCode == this.language);
            if(localized != null || localized != undefined)
                return localized;
            var lang = this.language.split('-')[0];
            if(lang)
            localized = list?.find(text => text.localeCode == lang)
            return localized;
        }
        return 
    }
}
